/**
 * LoginPage — auth-gated entry point.
 * Extracted from App.js in SPD v5.53 Phase D.
 *
 * Renders the login form + optional public signup toggle + Turnstile challenge.
 * `onLogin(username)` is called with the successful username; the parent
 * (App router) uses it to flip `isAuthenticated` state.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Turnstile from 'react-turnstile';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Upload, Search, Database, FileText, Zap, Download, LogOut, Lock, User, Menu, Settings, ClipboardList, SlidersHorizontal, BarChart2, TrendingUp, AlertTriangle, CheckCircle, XCircle, List, Circle, LayoutGrid, MapPin, Globe, ArrowLeftRight, Store, ShoppingCart, ChevronDown, ChevronUp, ExternalLink, Shield, Image as ImageIcon, Video, Play, ThumbsUp, ThumbsDown } from 'lucide-react';
import { toast } from 'sonner';
import { HoverTag } from '../components/ui/HoverTag';
import { brandConfig, applyBrandColors, applyBrandFont } from '../config/branding';
import VoiceActivation from '../components/VoiceActivation';
import { useIsMobile } from '../hooks/useIsMobile';

// Extracted Components
import { SourcesSection } from '../components/app/SourcesSection';
import { WebResultsSection } from '../components/app/WebResultsSection';
import { QuietModeChip } from '../components/QuietModeChip';
import {
  classifyCommand as classifyQuietCommand,
  getQuietMode,
  setQuietMode as persistQuietMode,
  hasSeenFirstTimeTip as hasSeenQuietTip,
  markFirstTimeTipSeen as markQuietTipSeen,
  COPY as QUIET_COPY,
} from '../lib/quietMode';
import { CitationModal } from '../components/app/CitationModal';
import { SuggestionsPanel } from '../components/app/SuggestionsPanel';
import { Walkthrough } from '../components/app/Walkthrough';
import { walkthroughCopy } from '../content/walkthrough';
import { RestoreTooltipDot } from '../components/app/RestoreTooltipDot';
import { ControlCentre } from '../components/app/ControlCentre';
import { MenuOverlay } from '../components/app/MenuOverlay';
import { SafeBrowser } from '../components/app/SafeBrowser';
import { QuotaDemoBox } from '../components/app/QuotaDemoBox';
import { SHOW_WALKTHROUGH, SHOW_DEMO_COUNTER, SHOW_CC_HINTS, SHOW_BETA_BANNERS } from '../featureFlags';
import { FloatingTopIcons } from '../components/app/FloatingTopIcons';
import { SessionExitBanner } from '../components/app/SessionExitBanner';
import { DemoDownloadCoachMark } from '../components/app/DemoDownloadCoachMark';
import { trackEvent } from '../utils/telemetry';

// Auth + citation helpers moved to utils/ in SPD v5.53 Phase D.
import { BACKEND_URL, API, getToken, setToken, clearToken, authAxios } from '../utils/authClient';
import { CITATION_RE, renderCitedChildren } from '../utils/citations';


const LoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // SPD v5.90 · LESSON-003 — signup fall-through removed. Signup lives at
  // /signup (dedicated route). The Turnstile / honeypot state below is
  // retained for compatibility with the shared form scaffold but is no
  // longer read from this page.
  const [turnstileToken, setTurnstileToken] = useState('');
  // Layer D (Feb 24, 2026) — Honeypot. Real users never see / fill this
  // (hidden in DOM but not via display:none so bots that respect that
  // still scrape the field). Bots that auto-fill every input get caught
  // server-side.
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [showWalkthrough, setShowWalkthrough] = useState(() => {
    // Feature flag (board-demo): when SHOW_WALKTHROUGH is off, never auto-show.
    if (!SHOW_WALKTHROUGH) return false;
    // D5-1 (2026-02-13) — operator directive: "Landing on Step 2 of 2
    // Not Step 1 of 2". The prior implementation stored the "seen" flag
    // in localStorage, so any returning visitor (or the operator during
    // a beta demo) skipped straight to the login form. Switched to
    // sessionStorage so the walkthrough replays on every fresh browser
    // session (new tab / new window / restart), while still not nagging
    // the user again during a single session. Users can still hit
    // "Replay walkthrough" to bring it back mid-session.
    try { return sessionStorage.getItem('si_walkthrough_seen') !== '1'; } catch { return true; }
  });
  const usernameRef = useRef(null);
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

  useEffect(() => { if (!showWalkthrough) usernameRef.current?.focus(); }, [showWalkthrough]);

  // IJ-070 stale-token hardening (2026-07-23) — if authedAxios redirected
  // us here with ?reason=session-expired, surface a clear toast so the
  // operator knows why they are being asked to sign in again.
  useEffect(() => {
    try {
      const reason = new URLSearchParams(window.location.search).get('reason');
      if (reason === 'session-expired') {
        toast.info('Your session expired. Please sign in again.', { duration: 5000 });
      }
    } catch (_) { /* noop */ }
  }, []);

  const completeWalkthrough = () => {
    // D5-1 (2026-02-13) — sessionStorage (not localStorage) so the flag
    // clears when the browser session ends. Also clear the older
    // localStorage flag so residual state from previous builds doesn't
    // linger and keep suppressing Step 1.
    try {
      sessionStorage.setItem('si_walkthrough_seen', '1');
      localStorage.removeItem('si_walkthrough_seen');
    } catch { /* noop */ }
    setShowWalkthrough(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) return;
    setIsLoading(true);
    try {
      // ── LOGIN only (SPD v5.90 · LESSON-003) ──────────────────────────
      // Prior versions silently fell through to /v1/beta/signup on 401,
      // which turned any username typo into a shadow beta_users row and
      // (worst case) locked founders out of their own admin account. The
      // fall-through is removed. Signup lives at its own /signup route
      // and is only reachable via an explicit user action (see the
      // "Create an account" link below the submit button).
      const res = await axios.post(`${API}/auth/login`, { username, password });
      setToken(res.data.token);
      onLogin(res.data.username);
      toast.success('Session started');
      // Honour `?returnTo=…` so a KC-page redirect brings the operator
      // back to where they were after signing in.
      try {
        const rt = new URLSearchParams(window.location.search).get('returnTo');
        if (rt && rt.startsWith('/') && !rt.startsWith('//')) {
          setTimeout(() => window.location.assign(rt), 400);
        }
      } catch (_) { /* noop */ }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      // Beta trial-exited (403 trial_exited) → redirect to marketing.
      if (status === 403 && detail && typeof detail === 'object' && detail.code === 'trial_exited') {
        const url = (detail.marketing_url || process.env.REACT_APP_MARKETING_URL || 'https://www.search.inc').trim();
        toast.error(detail.message || 'Your trial access has ended for this username.');
        setTimeout(() => window.location.assign(url), 1500);
        return;
      }
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Sign in failed');
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const c = walkthroughCopy.step3of3;

  return (
    <div data-testid="login-page" className="min-h-screen bg-black flex flex-col items-center justify-center px-4 py-10">
      {SHOW_WALKTHROUGH && showWalkthrough && <Walkthrough onComplete={completeWalkthrough} />}

      {/* Search.Inc brand mark · IJ-175 · 2026-07-30 · replaces the SVG magnifier + text wordmark plus tagline. Height 40px matches the previous letter cap footprint. */}
      <div className="text-center mb-8 sm:mb-12" data-testid="login-logo-block">
        <img
          src="/searchinc-logo.png"
          alt="Search.Inc — Actually not evil"
          style={{ height: 'clamp(40px, 5.07vw, 68px)' }}
          className="mx-auto drop-shadow-[0_0_24px_rgba(34,211,238,0.45)] select-none"
          draggable={false}
          data-testid="login-brand-logo"
        />
      </div>

      {/* Two-column: copy + form
          D2 (Jun 11 2026 SI Agentic prep) — the left instructional panel
          ("Create a Username and Password" + numbered onboarding steps) is
          gated behind SHOW_WALKTHROUGH. Beta-consumer copy isn't relevant
          to an enterprise air-gapped deployment. When hidden, the form
          panel centres itself (max-w-md). Re-enable by flipping
          REACT_APP_SHOW_WALKTHROUGH=true. */}
      <div className={`w-full grid grid-cols-1 gap-6 md:gap-10 items-stretch ${SHOW_WALKTHROUGH ? 'max-w-5xl md:grid-cols-2' : 'max-w-md'}`}>
        {/* Left: instructional copy */}
        {SHOW_WALKTHROUGH && (
        <div
          className="rounded-2xl p-6 sm:p-8 flex flex-col justify-between"
          style={{
            backgroundColor: 'rgba(8, 12, 24, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(0, 200, 255, 0.15)',
          }}
          data-testid="login-instructions-panel"
        >
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-cyan-300/80">{c.badge}</span>
            <h2 className="text-2xl sm:text-3xl font-bold text-cyan-400 mt-1 mb-5 leading-tight">
              {c.heading}
            </h2>
            <ol className="space-y-4">
              {c.instructions.map((item) => (
                <li key={item.n} className="flex gap-3 text-gray-100 leading-relaxed">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-cyan-400 text-black font-bold text-sm flex items-center justify-center mt-0.5">
                    {item.n}
                  </span>
                  <span className="text-base">{item.text}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className="mt-6 pt-5 border-t border-white/10">
            <p className="text-xs sm:text-sm text-gray-300 italic leading-relaxed">
              {c.psNote}
            </p>
            {c.tagline && (
              <p className="mt-3 text-xs sm:text-sm text-gray-300 italic leading-relaxed" data-testid="login-instructions-tagline">
                {c.tagline}
              </p>
            )}
          </div>
        </div>
        )}

        {/* Right: form */}
        <div
          className="rounded-2xl p-6 sm:p-8 flex flex-col justify-center"
          style={{
            backgroundColor: 'rgba(20, 25, 35, 0.85)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(34,211,238,0.18)',
          }}
          data-testid="login-form-panel"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <HoverTag
              content="Type in the exact username you just created"
              side="top"
              align="left"
              testId="login-username-hover-tag"
            >
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                ref={usernameRef}
                data-testid="login-username"
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="pl-10 bg-gray-950 border-gray-800 text-white placeholder:text-gray-600 focus:border-cyan-500 focus:ring-cyan-500/20"
              />
            </HoverTag>
            <HoverTag
              content="Type in the exact password you just created"
              side="top"
              align="left"
              testId="login-password-hover-tag"
            >
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                data-testid="login-password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 bg-gray-950 border-gray-800 text-white placeholder:text-gray-600 focus:border-cyan-500 focus:ring-cyan-500/20"
              />
            </HoverTag>
            <Button
              data-testid="login-submit"
              type="submit"
              disabled={isLoading}
              className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold transition-colors py-5 text-base"
            >
              {isLoading ? 'Starting session…' : c.submitLabel}
            </Button>

            {/* Phase 2 v5.14 — explicit deep-link for users arriving expecting
                a dedicated signup page (e.g. from the search.inc WP portal CTA).
                IJ-241 (2026-08-01) · upsized from an 11-pixel inline link to a
                full-width secondary CTA button matching the visual weight of
                the primary Begin session button. */}
            <a
              href="/signup"
              data-testid="login-create-account-link"
              className="mt-2 block w-full text-center rounded-md border-2 border-cyan-400 bg-transparent hover:bg-cyan-500/15 text-cyan-300 hover:text-cyan-200 font-semibold py-5 text-base transition-colors"
            >
              Create a new account
            </a>

            {/* Layer D (Feb 24, 2026) — Honeypot. Off-screen but real in
                the DOM so bots that fill every input get caught. Real
                users never see or touch it. */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
              <label htmlFor="website_url">Website</label>
              <input
                type="text"
                id="website_url"
                name="website_url"
                tabIndex={-1}
                autoComplete="off"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                data-testid="signup-honeypot"
              />
            </div>

            {/* Layer A (Feb 24, 2026) — Cloudflare Turnstile (Managed).
                Invisible for clean traffic; interactive challenge only
                for suspect traffic. No Google tracking. */}
            {turnstileSiteKey && (
              <div className="flex justify-center pt-2" data-testid="turnstile-widget">
                <Turnstile
                  sitekey={turnstileSiteKey}
                  onVerify={(token) => setTurnstileToken(token)}
                  onExpire={() => setTurnstileToken('')}
                  onError={() => setTurnstileToken('')}
                  refreshExpired="auto"
                  theme="dark"
                />
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Replay walkthrough — under both panels. Hidden by SHOW_WALKTHROUGH flag. */}
      {SHOW_WALKTHROUGH && !showWalkthrough && (
        <button
          type="button"
          onClick={() => setShowWalkthrough(true)}
          data-testid="replay-walkthrough-btn"
          className="mt-8 text-xs text-cyan-400/80 hover:text-cyan-300 underline underline-offset-4 transition-colors"
        >
          Replay walkthrough
        </button>
      )}
    </div>
  );
};

export default LoginPage;
